import React, { useEffect, useRef } from "react"
import { PageProps, graphql, Link } from "gatsby"
import Img from "gatsby-image"
import { kebabCase, startCase } from "lodash"
import "../styles/pages/blog-post.scss"
import "../styles/relatedPosts.css"
import {
  GridBlogRoll,
  HTMLContent,
  GraphQLData,
  BlogPostFrontmatter,
} from "@wp/ui"
import Layout from "../components/Layout"
import Icon from "@mdi/react"
import ReactWOW from "react-wow"
import "animate.css"
import { mdiFacebook, mdiTwitter } from "@mdi/js"
import useSiteMetadata from "../components/SiteMetadata"

const BlogPage: React.FC<PageProps<GraphQLData<BlogPostFrontmatter>>> = ({
  data,
  location,
}) => {
  const refs: any = useRef()
  const { markdownRemark, relatedTags } = data
  const { facebook, twitter, instagram } = useSiteMetadata()

  const {
    frontmatter: { title, description, tags, image, date, imageCredit },
    html,
  } = markdownRemark

  // fadeIn article with animate.css on scroll in view
  useEffect(() => {
    const DOMNode = refs.current
    let childrenElement: HTMLElement[]
    const animateIn = (childrenElement: HTMLElement[]): void => {
      childrenElement.forEach((element) => {
        if (window.innerHeight > element.getBoundingClientRect().y) {
          element.style.animationName = "fadeIn"
          element.style.visibility = "visible"
        }
      })
    }
    if (DOMNode) {
      childrenElement = DOMNode.querySelector(".article-text").children
      childrenElement = Array.from(childrenElement)
      childrenElement.forEach((element) => {
        element.classList.add("animate__animated")
        element.classList.add("fadeIn")
        element.style.visibility = "hidden"
      })

      window.addEventListener("scroll", () => {
        animateIn(childrenElement)
      })
    }
    return (): void => {
      window.removeEventListener("scroll", () => {
        animateIn(childrenElement)
      })
    }
  }, [])

  return (
    <Layout
      id="blog"
      title={title}
      leftSide={
        <ReactWOW animation="fadeIn">
          <figure className="cover-img animate__animated">
            <Img fluid={image.childImageSharp.fluid} alt="" />
            <div className="cover-details ">
              <h1 className="font-lg mobile">{title}</h1>
              <HTMLContent content={imageCredit} className="font-xs credit" />
              <section className="share">
                {(facebook || twitter || instagram) && (
                  <div>
                    <a
                      className="btn btn-box"
                      href={`https://www.facebook.com/sharer/sharer.php?u=${location.href}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Icon path={mdiFacebook} size={1} color="#fff" />
                    </a>
                    <a
                      className="btn btn-box"
                      href={`http://www.twitter.com/share?text=${title}&url=${location.href}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Icon path={mdiTwitter} size={1} color="#fff" />
                    </a>
                  </div>
                )}
              </section>
            </div>
          </figure>
        </ReactWOW>
      }
      rightSide={
        <>
          <section className="blog-post-container page-container">
            <div></div>
            <article ref={refs}>
              <section className="heading animate__animated">
                <HTMLContent content={imageCredit} className="font-xs credit" />
                <h1 className="font-lg">{title}</h1>
                <div className="font-xs tags">
                  <p>
                    {tags.map((tag: string, idx: number) => (
                      <React.Fragment key={idx}>
                        <Link to={`tags/${kebabCase(tag)}`}>
                          {startCase(tag)}
                        </Link>
                        {tags.length - 1 !== idx && ","}
                      </React.Fragment>
                    ))}
                  </p>
                </div>
                <p className="font-xs description">{description}</p>
                <p className="font-xs date">{date}</p>
                <div className="mobile font-xs mobile-share">
                  <p>
                    Share on{" "}
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${location.href}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      facebook
                    </a>{" "}
                    and{" "}
                    <a
                      href={`http://www.twitter.com/share?text=${title}&url=${location.href}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      twitter
                    </a>
                  </p>
                </div>
              </section>

              <HTMLContent content={html} className="article-text" />
            </article>
          </section>

          <GridBlogRoll
            id="related-posts"
            title="Related Posts"
            posts={relatedTags.edges.slice(0, 3)}
          />
        </>
      }
    />
  )
}

export default BlogPage

export const pageQuery = graphql`
  query BlogPostByID($id: String!, $tags: [String]!) {
    markdownRemark(id: { eq: $id }) {
      id
      html
      frontmatter {
        title
        templateKey
        date(formatString: "MMMM DD, YYYY")
        featuredpost
        description
        tags
        image {
          childImageSharp {
            fluid(maxWidth: 1200, quality: 70) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        imageCredit
      }
    }

    relatedTags: allMarkdownRemark(
      sort: { order: DESC, fields: [frontmatter___date] }
      limit: 2000
      filter: {
        frontmatter: { templateKey: { eq: "blog-post" }, tags: { in: $tags } }
      }
    ) {
      totalCount
      edges {
        node {
          id
          fields {
            slug
          }
          frontmatter {
            title
            templateKey
            date(formatString: "MMMM DD, YYYY")
            featuredpost
            description
            image {
              childImageSharp {
                fluid(maxWidth: 1200, quality: 30) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
            imageCredit
          }
        }
      }
    }
  }
`
